import { assignInlineVars } from "@vanilla-extract/dynamic";
import clsx from "clsx";
import React, { useRef, useState } from "react";
import useIsomorphicLayoutEffect from "web/react/hooks/use-isomorphic-layout-effect/use-isomorphic-layout-effect";
import { getSafariMajorVersion } from "web/react/utils/user-agent";
import environment from "web/script/modules/environment";
import * as styles from "./product-gallery-image.css";

export function buildUrl(url: string, width: number, height: number): string {
    return url.replace("/photos/", `/${width}/${height}/n/photos/`);
}
export interface Image {
    id: string | number;
    altText?: string | null;
    url: string;
    src?: string;
}

export interface ProductGalleryImageProps {
    src: string;
    alt?: string;
    onClick?: (event: React.MouseEvent<HTMLImageElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLImageElement>) => void;
    width?: number;
    height?: number;
    isFullscreen?: boolean;
    lazy?: boolean;
    blurImageOnLoad?: boolean;
    tabIndex?: number;
}

export function ProductGalleryImage({
    src,
    alt,
    lazy = false,
    onClick,
    onKeyDown,
    width = 520,
    height = 650,
    isFullscreen = false,
    blurImageOnLoad = false,
    tabIndex = 0,
}: ProductGalleryImageProps): React.ReactElement {
    const [isLoadingComplete, setIsLoadingComplete] = useState<boolean | undefined>(undefined);
    const imgRef = useRef<HTMLImageElement>(null);
    const isIOS18 = getSafariMajorVersion() === 18;
    const mainImageLoading = isIOS18 || !lazy ? "eager" : "lazy";

    // CW-2172 increase dimension of images in IS PDP
    const showHigherResolutionImages = environment.getFeature(
        "seo_higher_resolution_pdp_images_uk"
    );
    const showHighQualityFullscreenImages = !!(
        isFullscreen && environment.getFeature("beam_web_pdp_gallery_fullscreen_quality_increase")
    );
    const mobileRatio = { width: 520, height: 650 };
    const desktopRatio = { width: 1040, height: 1300 };
    const fullscreenRatio = { width: 2400, height: 3000 };
    const responsiveMobileRatio = showHighQualityFullscreenImages ? fullscreenRatio : mobileRatio;
    const responsiveDesktopRatio = showHighQualityFullscreenImages ? fullscreenRatio : desktopRatio;

    const wrapperClassName = clsx({
        [styles.item]: !isFullscreen,
        [styles.fullScreenItem]: isFullscreen,
    });

    const imgClassNames = clsx({
        [styles.image]: !isFullscreen,
        [styles.fullscreenImage]: isFullscreen,
        [styles.mainImage]: blurImageOnLoad && isLoadingComplete === false,
        [styles.mainImageLoaded]: blurImageOnLoad && isLoadingComplete === true,
        [styles.blurTransition]: blurImageOnLoad && isLoadingComplete === false,
    });

    const blurImageSize = { width: 48, height: 60 };
    const blurContainerClassName = blurImageOnLoad
        ? clsx(styles.blurImageContainer, {
              [styles.blurImageLoading]: isLoadingComplete === false,
              [styles.blurImageContainerLoaded]: isLoadingComplete === true,
          })
        : undefined;
    const blurContainerStyle = blurImageOnLoad
        ? assignInlineVars({
              [styles.containerWidth]: `${width}px`,
              [styles.blurImageUrl]: `url(${buildUrl(
                  src,
                  blurImageSize.width,
                  blurImageSize.height
              )})`,
          })
        : undefined;

    useIsomorphicLayoutEffect(() => {
        if (!blurImageOnLoad) return;
        if (isIOS18) {
            setIsLoadingComplete(true);
            return;
        }
        setIsLoadingComplete(imgRef.current?.complete ?? false);
        imgRef.current
            ?.decode()
            .then(() => setIsLoadingComplete(true))
            .catch(() => setIsLoadingComplete(true));
    }, [blurImageOnLoad, isIOS18]);

    return (
        <div
            className={wrapperClassName}
            onClick={onClick}
            onKeyDown={onKeyDown}
            role="button"
            tabIndex={tabIndex}
            data-testid="product-gallery-image"
        >
            {blurImageOnLoad && isLoadingComplete === false && (
                <span className={styles.loadingSpinner} aria-hidden="true" />
            )}
            <div className={blurContainerClassName} style={blurContainerStyle}>
                {showHigherResolutionImages ? (
                    <picture data-testid="product-gallery-image-picture" className={imgClassNames}>
                        <source
                            media={styles.mobileMedia}
                            srcSet={buildUrl(
                                src,
                                responsiveMobileRatio.width,
                                responsiveMobileRatio.height
                            )}
                        />
                        <source
                            media={styles.desktopMedia}
                            srcSet={buildUrl(
                                src,
                                responsiveDesktopRatio.width,
                                responsiveDesktopRatio.height
                            )}
                        />
                        <img
                            ref={imgRef}
                            decoding="async"
                            alt={alt}
                            width={width}
                            src={buildUrl(
                                src,
                                responsiveDesktopRatio.width,
                                responsiveDesktopRatio.height
                            )}
                            loading={mainImageLoading}
                            data-testid="product-gallery-image-img"
                        />
                    </picture>
                ) : (
                    <img
                        className={imgClassNames}
                        ref={imgRef}
                        decoding="async"
                        alt={alt}
                        width={width}
                        src={buildUrl(src, width, height)}
                        loading={mainImageLoading}
                        data-testid="product-gallery-image-img"
                    />
                )}
            </div>
        </div>
    );
}
